// Reports — Office insight. Hours over a date range, two ways, plus the
// recent-activity feed. docs/REBUILD_PARITY.md rows "Reports with filters ·
// export CSV" and "Recent activity feed".
//
//   GET /api/dashboard/stats                      the Stat row (NOT range-scoped)
//   GET /api/time-entries/reports/summary?...     job x worker rows (seconds)
//   GET /api/time-entries?...                     one row per shift, the detail view
//   GET /api/dashboard/weekly-chart               hours per day, current week
//   GET /api/dashboard/activity-feed?limit=20     the activity feed (every role)
//   GET /api/admin/users                          worker filter
//   GET /api/jobs/all                             job filter (admin/manager)
//
// The paths, the parameters each handler actually reads, and the roles each
// narrows to "your own rows" are declared in ./reportsContract.js and checked
// against the route files by OfficeReportsDetail.test.js. Read that file's
// header before changing anything here — the two time-entry endpoints gate
// DIFFERENTLY from one another, which is why this screen says out loud whose
// rows the detail list contains.
//
// Date maths: the company timezone and start_of_week from settings, through
// client/src/utils/weekUtils.js. Dates here are YYYY-MM-DD strings and are
// never handed to `new Date()`.

import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSettings } from '../../../App';
import { useRole, useCan } from '../../../hooks/usePermission';
import { DataTable, Stat, Pill, Notice, FwButton, FwField, FwInput, FwSelect, Skeleton } from '../../../ui/fw';
import { getCompanyDateConfig, getCurrentWeekRange, todayInTz, formatLocalDateShort } from '../../../utils/weekUtils';
import { formatDateOnly, formatTimeInUserTZ } from '../../../utils/timezone';
import {
  useOfficeQuery, downloadText, csvLine, PageHeader, Toolbar, Panel, LoadError, pickerNote, hoursOf, fmtDateTime,
} from '../records/OfficePage';
import {
  VIEWS, FEED_LIMIT,
  summaryPath, entriesPath, feedPath,
  rowsOf, entriesScopeNote, summaryScopeNote, feedScopeNote, feedEmptyText,
  summaryHours, summaryHoursTitle, avgPerEntry, avgPerEntryTitle,
  actionLabel, timeAgo, isRunning, gpsText, noteBits, entriesCsv, csvName,
} from './reportsContract';

const PRESETS = [
  { key: 'today', label: 'Today' },
  { key: 'week', label: 'This week' },
  { key: 'month', label: 'This month' },
  { key: 'year', label: 'This year' },
  { key: 'custom', label: 'Custom' },
];

const DASH = '—';

/** Pure: the YYYY-MM-DD range a preset means, given settings and today. */
export function rangeFor(preset, settings, custom = {}) {
  const { timezone } = getCompanyDateConfig(settings);
  const today = todayInTz(timezone);
  switch (preset) {
    case 'today': return { start: today, end: today };
    case 'week': { const w = getCurrentWeekRange(settings); return { start: w.startDate, end: w.endDate }; }
    case 'month': return { start: `${today.slice(0, 7)}-01`, end: today };
    case 'year': return { start: `${today.slice(0, 4)}-01-01`, end: today };
    case 'custom': return { start: custom.start || today, end: custom.end || today };
    default: return { start: today, end: today };
  }
}

/**
 * Pure: the CSV for the summary rows plus a total line.
 *
 * `Avg per Entry (Hours)` is APPENDED rather than slotted in where the classic
 * screen had it (client/src/components/Reports/Reports.js:272 put it fourth,
 * between the entry count and the estimate). The four columns this export
 * already wrote keep their positions, so a spreadsheet somebody built against
 * hours-*.csv and reads by column index does not silently start reading the
 * wrong field. See docs/PARITY_2026-09-08.md §10, and reportsContract.js's
 * avgPerEntry for why `Estimated` and `Variance` are not here.
 *
 * The average is BLANK, not 0.00, for a row with no completed shift — the same
 * choice entriesCsv makes for a running duration, for the same reason: a
 * spreadsheet cell reading 0.00 is a claim about work that was done.
 */
export function summaryCsv(rows) {
  const lines = [csvLine(['Job', 'Worker', 'Entries', 'Hours', 'Avg per Entry (Hours)'])];
  const blankDash = (v) => (v === DASH ? '' : v);
  let entries = 0; let seconds = 0; let running = 0;
  rows.forEach((r) => {
    entries += Number(r.entry_count) || 0;
    seconds += Number(r.total_duration) || 0;
    running += Number(r.running_count) || 0;
    lines.push(csvLine([
      r.job_title || 'No job',
      r.user_name || 'Unknown',
      r.entry_count ?? 0,
      hoursOf(r.total_duration),
      blankDash(avgPerEntry(r, hoursOf)),
    ]));
  });
  // The total line's average is the mean shift across the whole range, taken
  // through the same helper against the summed row so the two cannot disagree.
  const total = { entry_count: entries, total_duration: seconds, running_count: running };
  lines.push(csvLine(['Total', '', entries, hoursOf(seconds), blankDash(avgPerEntry(total, hoursOf))]));
  return `${lines.join('\n')}\n`;
}

function BarChart({ data }) {
  const W = 560; const H = 180; const padL = 8; const padB = 28; const padT = 18;
  const n = data.length || 1;
  const slot = (W - padL * 2) / n;
  const barW = Math.max(12, slot * 0.6);
  const max = Math.max(1, ...data.map((d) => Number(d.hours) || 0));
  const scale = (H - padB - padT) / max;
  return (
    <svg className="or-chart" viewBox={`0 0 ${W} ${H}`} role="img" aria-label="Hours per day this week">
      <line x1={padL} x2={W - padL} y1={H - padB} y2={H - padB} className="or-chart__axis" strokeWidth="1" />
      {data.map((d, i) => {
        const h = (Number(d.hours) || 0) * scale;
        const x = padL + i * slot + (slot - barW) / 2;
        const y = H - padB - h;
        return (
          <g key={d.date || i}>
            <rect x={x} y={y} width={barW} height={h} rx="3" fill="var(--fw-signal)" />
            <text x={x + barW / 2} y={y - 5} textAnchor="middle" className="or-chart__value">{Number(d.hours) ? hoursOf(d.hours * 3600) : ''}</text>
            <text x={x + barW / 2} y={H - padB + 16} textAnchor="middle" className="or-chart__label">{d.day}</text>
          </g>
        );
      })}
    </svg>
  );
}

const TOTAL_KEY = '__total';

/**
 * A panel body that must never render an unreadable answer as an empty one.
 *
 * `rows === null` here means the request answered 200 with something that is
 * not an array — a failure wearing a success. Falling through to the table's
 * own empty text would report it as "No time was tracked in this range", which
 * is a measurement made from no measurement. `error` and `pending` are the
 * ordinary two; the fourth branch is the one that gets deleted by accident.
 */
function QueryBody({ query, rows, ready, notReadyText, errorText, unreadableText, children }) {
  // The table below is edge-to-edge in a `--flush` panel; a sentence must not
  // be, so every message branch gets the panel's own padding back.
  const say = (node) => <div className="or-panel__inset">{node}</div>;
  if (!ready) return say(<p className="or-muted">{notReadyText}</p>);
  if (query.isPending) return say(<Skeleton lines={4} />);
  if (query.error) return say(<LoadError error={query.error} fallback={errorText} />);
  if (rows === null) return say(<Notice tone="stop" data-panel-error="unreadable">{unreadableText}</Notice>);
  return children;
}

export default function Reports() {
  const { settings } = useSettings();
  const role = useRole();
  // The same questions routes/timeEntries.js asks: hasClockOversight for the
  // detailed list, and that OR reports.read for the summary, which is wider on
  // purpose (accounting holds reports.read and no clock oversight, so it sees
  // company totals but only its own entries). Asked as capabilities so a
  // person holding a GRANT is not told their complete list is incomplete.
  //
  // Called unconditionally and combined afterwards: `a() || b()` short-circuits
  // and a skipped hook changes hook ORDER between renders, which
  // react-hooks/rules-of-hooks fails the build over — correctly.
  const clockManageAll = useCan('time_entries', 'manage_all');
  const clockManageTeam = useCan('time_entries', 'manage_team');
  const reportsRead = useCan('reports', 'read');
  const mayOpenActivityLog = useCan('activity_log', 'read');
  const navigate = useNavigate();
  const [view, setView] = useState('summary');
  const [preset, setPreset] = useState('week');
  const [custom, setCustom] = useState({ start: '', end: '' });
  const [userId, setUserId] = useState('');
  const [jobId, setJobId] = useState('');

  const range = useMemo(() => rangeFor(preset, settings, custom), [preset, settings, custom]);
  const validRange = !!range.start && !!range.end && range.start <= range.end;
  const detailed = view === 'detailed';

  const statsQ = useOfficeQuery(['dashboard', 'stats'], '/dashboard/stats');
  const chartQ = useOfficeQuery(['dashboard', 'weekly-chart'], '/dashboard/weekly-chart');
  const usersQ = useOfficeQuery(['admin', 'users'], '/admin/users');
  const jobsQ = useOfficeQuery(['jobs', 'all'], '/jobs/all');
  const feedQ = useOfficeQuery(['dashboard', 'activity-feed', FEED_LIMIT], feedPath(FEED_LIMIT));

  // The key carries the whole path, so changing a filter starts a NEW query
  // rather than overwriting the running one's rows — which is the
  // last-arrival-wins race the classic screen hand-rolls a sequence number for
  // (client/src/components/Reports/Reports.js:87). Only the view on screen is
  // fetched; react-query has the other one cached when it is switched back to.
  const sPath = summaryPath({ start: range.start, end: range.end, userId, jobId });
  const ePath = entriesPath({ start: range.start, end: range.end, userId, jobId });
  const summaryQ = useOfficeQuery(['reports', 'summary', sPath], sPath, { enabled: validRange && !detailed });
  const entriesQ = useOfficeQuery(['reports', 'entries', ePath], ePath, { enabled: validRange && detailed });

  // null means "not an array" — never `|| []`. See rowsOf in reportsContract.js.
  const summaryRows = rowsOf(summaryQ.data);
  const entryRows = rowsOf(entriesQ.data);

  const summary = useMemo(() => summaryRows || [], [summaryRows]);
  // `running` is summed alongside the other two so the Total row can answer
  // the average through the same helper every other row uses — a Total that
  // divided by entry_count while the rows divided by completed shifts would
  // disagree with the column above it.
  const totals = useMemo(() => summary.reduce(
    (t, r) => ({
      entries: t.entries + (Number(r.entry_count) || 0),
      seconds: t.seconds + (Number(r.total_duration) || 0),
      running: t.running + (Number(r.running_count) || 0),
    }),
    { entries: 0, seconds: 0, running: 0 }
  ), [summary]);
  const tableRows = useMemo(() => (summary.length === 0 ? [] : [
    ...summary,
    {
      [TOTAL_KEY]: true,
      job_title: 'Total',
      user_name: '',
      entry_count: totals.entries,
      total_duration: totals.seconds,
      running_count: totals.running,
    },
  ]), [summary, totals]);

  const bold = (fn) => (r) => (r[TOTAL_KEY] ? <strong>{fn(r)}</strong> : fn(r));
  // As a card: the job is the headline, the worker the line under it, and the
  // two numbers stay LABELLED facts. Hours especially — an unlabelled "12.50"
  // in a card slot is a figure somebody reads back on payday, and it must say
  // what it is. The Total row keeps its own shape: "Total" as the headline
  // with an empty worker line, which is what it is in the table too.
  const summaryColumns = [
    { key: 'job_title', label: 'Job', role: 'title', render: bold((r) => r.job_title || 'No job') },
    { key: 'user_name', label: 'Worker', role: 'subtitle', render: bold((r) => r.user_name || (r[TOTAL_KEY] ? '' : 'Unknown')) },
    { key: 'entry_count', label: 'Entries', align: 'right', role: 'fact', render: bold((r) => r.entry_count ?? 0) },
    {
      key: 'total_duration',
      label: 'Hours',
      align: 'right',
      role: 'fact',
      // Not a bare hoursOf: a row whose only shift is still running reported
      // 0.00, which reads as "worked none" about somebody who is out there
      // working. See summaryHours in reportsContract.js — the completed total
      // is unchanged in every case; what is added is the ability to tell
      // "none" from "not finished yet".
      render: bold((r) => {
        const title = summaryHoursTitle(r);
        const text = summaryHours(r, hoursOf);
        return title ? <span title={title} aria-label={title}>{text}</span> : text;
      }),
    },
    {
      // The classic screen's "Avg per Entry" (docs/PARITY_2026-09-08.md §10).
      // Third and last fact on the card — RecordList shows up to three, and
      // Entries / Hours / Avg is exactly the set, so nothing is pushed off.
      // See avgPerEntry in reportsContract.js for why it divides by completed
      // shifts and why Estimated / Variance are not beside it.
      key: 'avg_per_entry',
      label: 'Avg / entry',
      align: 'right',
      role: 'fact',
      render: bold((r) => {
        const title = avgPerEntryTitle(r);
        const text = avgPerEntry(r, hoursOf);
        return title ? <span title={title} aria-label={title}>{text}</span> : text;
      }),
    },
  ];

  // Hours on a RUNNING entry is a dash and not 0.00. `duration` is NULL until
  // the shift is stopped, and 0.00 in an hours column is a claim that somebody
  // worked none — the exact reading a crew member disputes on payday.
  //
  // ── As a card ──────────────────────────────────────────────────────────────
  // A shift is "who worked on what, when, for how long", so: the job is the
  // headline, the worker is the line under it, the DATE is the badge (a card
  // reads as one day's work and the date says which), and Start / End / Hours
  // are the three facts.
  //
  // `end_time` is deliberately NOT the badge even though it is the column that
  // renders the "Active" pill. In the badge slot a finished shift would put a
  // bare "12:30 PM" top-right with nothing saying whether that is when it
  // started or when it stopped — an unlabelled time on a payroll screen. As a
  // labelled fact it reads "END · Active" while running and "END · 12:30 PM"
  // when it is not, and the running state is still on the card without
  // scrolling, which is what APP_REBUILD.md §3.3 asks for.
  //
  // Hidden — table-only, both because the card holds three facts and because
  // neither survives a 9rem slot: `gps` is a coordinate pair or a place name
  // that truncates to nothing, `notes` renders one block per note and would
  // set the card's height from a free-text field. Both are in the table at
  // desktop width and in the CSV export at every width.
  const entryColumns = [
    { key: 'date', label: 'Date', role: 'badge', render: (e) => formatDateOnly(e.start_time) || DASH },
    { key: 'job_title', label: 'Job', role: 'title', render: (e) => e.job_title || 'No job' },
    { key: 'user_name', label: 'Worker', role: 'subtitle', render: (e) => e.user_name || 'Unknown' },
    { key: 'start_time', label: 'Start', role: 'fact', render: (e) => formatTimeInUserTZ(e.start_time) || DASH },
    {
      key: 'end_time',
      label: 'End',
      role: 'fact',
      render: (e) => (isRunning(e) ? <Pill tone="now">Active</Pill> : (formatTimeInUserTZ(e.end_time) || DASH)),
    },
    { key: 'duration', label: 'Hours', align: 'right', role: 'fact', render: (e) => (isRunning(e) ? DASH : hoursOf(e.duration)) },
    { key: 'gps', label: 'Location', role: 'hidden', render: (e) => gpsText(e) || DASH },
    {
      key: 'notes',
      label: 'Notes',
      role: 'hidden',
      render: (e) => {
        const bits = noteBits(e);
        if (bits.length === 0) return DASH;
        return <>{bits.map((b, i) => <div key={i} className="or-feed__meta">{b}</div>)}</>;
      },
    },
  ];

  const s = statsQ.data;
  const stats = s ? [
    { label: 'Today', value: hoursOf(s.todayTime), hint: 'hours' },
    { label: 'This week', value: hoursOf(s.weekTime), hint: 'hours', tone: 'now' },
    { label: 'Last week', value: hoursOf(s.lastWeekTime), hint: s.weekChangePercent !== undefined && s.weekChangePercent !== null ? `${s.weekChangePercent > 0 ? '+' : ''}${Math.round(Number(s.weekChangePercent))}% vs this week` : 'hours' },
    { label: 'Active jobs', value: s.activeJobs ?? 0, hint: `${s.totalJobs ?? 0} total` },
    { label: 'Clocked in', value: s.clockedInCount ?? 0, hint: `${s.totalWorkers ?? 0} workers`, tone: (s.clockedInCount ?? 0) > 0 ? 'ok' : 'neutral' },
    { label: 'Timesheets waiting', value: s.pendingTimesheets ?? 0, tone: (s.pendingTimesheets ?? 0) > 0 ? 'warn' : 'neutral' },
  ] : [];

  const rangeLabel = validRange
    ? (range.start === range.end ? formatLocalDateShort(range.start, true) : `${formatLocalDateShort(range.start, true)} to ${formatLocalDateShort(range.end, true)}`)
    : 'Pick a start and end date';

  const activeRows = detailed ? entryRows : summaryRows;
  const exportCsv = () => {
    const text = detailed
      ? entriesCsv(entryRows || [], { date: formatDateOnly, time: formatTimeInUserTZ, hours: hoursOf }, csvLine)
      : summaryCsv(summary);
    downloadText(text, csvName(view, range));
  };

  const users = (usersQ.data || []).filter((u) => u.is_active === 1 || u.is_active === true || u.is_active === undefined);
  const jobs = jobsQ.data || [];

  const seesAllEntries = clockManageAll || clockManageTeam;
  const seesAllSummary = seesAllEntries || reportsRead;
  const detailNote = entriesScopeNote(seesAllEntries);
  const summaryNote = summaryScopeNote(seesAllSummary);
  const feedRows = rowsOf(feedQ.data);

  return (
    <div className="or-page" data-screen="office-Reports">
      <PageHeader title="Reports" meta={rangeLabel}>
        <FwButton
          variant="ghost"
          icon="download"
          onClick={exportCsv}
          disabled={!activeRows || activeRows.length === 0}
        >
          Export CSV
        </FwButton>
      </PageHeader>

      {statsQ.isPending ? <Skeleton lines={2} /> : null}
      <LoadError error={statsQ.error} fallback="Could not load the totals." />
      {stats.length > 0 ? (
        <>
          <div className="or-stats">
            {stats.map((st) => <Stat key={st.label} label={st.label} value={st.value} hint={st.hint} tone={st.tone || 'neutral'} />)}
          </div>
          {/*
            These six come from /api/dashboard/stats, which takes no date
            parameters at all — they are the company's position right now. Sat
            directly above a date-range picker with no caption they read as the
            range's totals, which is a wrong figure that looks like a right one.
          */}
          <p className="or-muted" data-note="stats-scope">
            These are the company&apos;s current figures. They do not follow the date range below.
          </p>
        </>
      ) : null}

      <Toolbar>
        {/*
          Both groups are LABELLED. Side by side and unlabelled they read as one
          group with two active buttons — "Detailed entries" and "This week"
          both highlighted, nothing saying they answer different questions.
          Invisible to jsdom; obvious in a browser at 390px and at 1440px.
        */}
        <div className="or-segset">
          <span className="or-segset__label" id="or-reports-view-label">Report</span>
          <div className="or-seg" role="group" aria-labelledby="or-reports-view-label">
            {VIEWS.map((v) => (
              <FwButton key={v.key} variant={view === v.key ? 'primary' : 'ghost'} onClick={() => setView(v.key)} aria-pressed={view === v.key}>
                {v.label}
              </FwButton>
            ))}
          </div>
        </div>
        <div className="or-segset">
          <span className="or-segset__label" id="or-reports-range-label">Date range</span>
          <div className="or-seg" role="group" aria-labelledby="or-reports-range-label">
            {PRESETS.map((p) => (
              <FwButton key={p.key} variant={preset === p.key ? 'primary' : 'ghost'} onClick={() => setPreset(p.key)} aria-pressed={preset === p.key}>
                {p.label}
              </FwButton>
            ))}
          </div>
        </div>
        {preset === 'custom' ? (
          <>
            <FwField label="From" htmlFor="or-reports-start">
              <FwInput type="date" value={custom.start} onChange={(e) => setCustom((c) => ({ ...c, start: e.target.value }))} />
            </FwField>
            <FwField label="To" htmlFor="or-reports-end">
              <FwInput type="date" value={custom.end} onChange={(e) => setCustom((c) => ({ ...c, end: e.target.value }))} />
            </FwField>
          </>
        ) : null}
        {/*
          Both of these lists can fail on an ordinary day, and an empty picker
          reads as "there are none" rather than as "nobody answered":
          /admin/users is admin/manager/accounting only, and /jobs/all refuses
          `accounting` outright (server/routes/jobs.js), so a bookkeeper opening
          Reports gets a 403 on the job list every single time.
        */}
        <FwField label="Worker" htmlFor="or-reports-worker" error={pickerNote(usersQ.error, 'worker')}>
          <FwSelect value={userId} onChange={(e) => setUserId(e.target.value)}>
            <option value="">All workers</option>
            {users.map((u) => <option key={u.id} value={String(u.id)}>{u.name}</option>)}
          </FwSelect>
        </FwField>
        <FwField label="Job" htmlFor="or-reports-job" error={pickerNote(jobsQ.error, 'job')}>
          {/*
            Disabled only when the list really came back and was empty. Greying
            the control out because the request FAILED is the same lie one step
            further on: it says "there is nothing to pick here", which is a
            statement about the data made from no data at all.
          */}
          <FwSelect value={jobId} onChange={(e) => setJobId(e.target.value)} disabled={!jobsQ.error && jobs.length === 0}>
            <option value="">All jobs</option>
            {jobs.map((j) => <option key={j.id} value={String(j.id)}>{j.title}</option>)}
          </FwSelect>
        </FwField>
      </Toolbar>

      {detailed ? (
        <Panel className="or-panel--flush" title="Every entry in this range">
          {/*
            GET /api/time-entries narrows to the caller's own rows for every
            role but admin and manager, and IGNORES ?user_id in that branch
            (server/routes/timeEntries.js:41). The summary above does not — it
            narrows `worker` alone. So a bookkeeper reading a company-wide
            summary and then an empty detail list below it would conclude the
            crew logged nothing. Null for admin and manager, so a complete list
            is never labelled incomplete.
          */}
          {detailNote ? (
            <div className="or-panel__inset">
              <Notice tone="warn" data-note="entries-scope">{detailNote}</Notice>
            </div>
          ) : null}
          <QueryBody
            query={entriesQ}
            rows={entryRows}
            ready={validRange}
            notReadyText="Pick a start and end date, start first."
            errorText="Could not load the entries."
            unreadableText="The server's answer could not be read, so no entries are shown. This is not an empty range."
          >
            <DataTable
              columns={entryColumns}
              rows={entryRows || []}
              rowKey={(e, i) => e.id ?? i}
              empty="No time was tracked in this range."
              caption="Every time entry in this range"
            />
          </QueryBody>
        </Panel>
      ) : (
        <Panel className="or-panel--flush" title="Hours by job and worker">
          {summaryNote ? (
            <div className="or-panel__inset">
              <Notice tone="warn" data-note="summary-scope">{summaryNote}</Notice>
            </div>
          ) : null}
          <QueryBody
            query={summaryQ}
            rows={summaryRows}
            ready={validRange}
            notReadyText="Pick a start and end date, start first."
            errorText="Could not load the summary."
            unreadableText="The server's answer could not be read, so no hours are shown. This is not an empty range."
          >
            <DataTable
              columns={summaryColumns}
              rows={tableRows}
              rowKey={(r, i) => (r[TOTAL_KEY] ? 'total' : `${r.job_id}-${r.user_id}-${i}`)}
              empty="No time was tracked in this range."
              caption="Hours by job and worker"
            />
          </QueryBody>
        </Panel>
      )}

      <Panel title="This week, by day">
        {chartQ.isPending ? <Skeleton lines={3} /> : null}
        <LoadError error={chartQ.error} fallback="Could not load the weekly chart." />
        {chartQ.data ? <BarChart data={chartQ.data} /> : null}
      </Panel>

      <Panel
        title="Recent activity"
        actions={mayOpenActivityLog ? (
          <FwButton variant="ghost" icon="list" onClick={() => navigate('/activity')}>View all</FwButton>
        ) : null}
      >
        {/*
          /api/dashboard/activity-feed rather than /api/activity/recent: the
          latter is behind an inline admin/manager 403, so on a screen the
          navigation offers to `accounting` it was a guaranteed refusal on every
          load. This one answers for every role — narrowed to the caller's own
          rows outside admin/manager, which is why the scope is stated rather
          than left to be inferred from a short list.
        */}
        <p className="or-muted" data-note="feed-scope">{feedScopeNote(role)}</p>
        {feedQ.isPending ? <Skeleton lines={4} /> : null}
        <LoadError error={feedQ.error} fallback="Could not load recent activity." />
        {!feedQ.isPending && !feedQ.error && feedRows === null ? (
          <Notice tone="stop" data-panel-error="activity-feed">
            The server&apos;s answer could not be read, so no activity is shown. This is not an empty feed.
          </Notice>
        ) : null}
        {feedRows && feedRows.length === 0 ? <p className="or-muted" data-empty="feed">{feedEmptyText(role)}</p> : null}
        {feedRows && feedRows.length > 0 ? (
          <ul className="or-feed">
            {feedRows.map((a, i) => {
              const ago = timeAgo(a.created_at);
              const when = fmtDateTime(a.created_at);
              return (
                <li key={a.id ?? i} className="or-feed__item" data-activity={a.id}>
                  <div className="or-feed__text">
                    <Pill tone="neutral">{actionLabel(a.action_type)}</Pill>{' '}
                    {a.description || a.entity_name || actionLabel(a.action_type)}
                  </div>
                  <div className="or-feed__meta">
                    {[a.user_name, ago || when].filter(Boolean).join(' · ')}
                  </div>
                </li>
              );
            })}
          </ul>
        ) : null}
      </Panel>
    </div>
  );
}
